import OpenAI from 'openai';

/**
 * Enhanced OpenAI client initialization with comprehensive validation
 * @returns {OpenAI} Configured OpenAI client instance.
 */
let openai = null;
let clientError = null;

try {
  const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
  
  // Enhanced API key validation
  if (!apiKey) {
    clientError = { type: 'no_api_key', message: 'OpenAI API key not found in environment variables' };
  } else if (apiKey === 'your-openai-api-key-here') {
    clientError = { type: 'placeholder_key', message: 'API key is still a placeholder - please add your actual OpenAI API key' };
  } else if (!apiKey?.startsWith('sk-')) {
    clientError = { type: 'invalid_format', message: 'Invalid API key format - OpenAI keys should start with "sk-"' };
  } else if (apiKey?.length < 20) {
    clientError = { type: 'invalid_length', message: 'API key appears to be too short' };
  } else if (apiKey?.includes(' ') || apiKey?.includes('\n') || apiKey?.includes('\t')) {
    clientError = { type: 'corrupted_key', message: 'API key contains invalid characters (spaces, newlines, or tabs)' };
  } else {
    // Initialize OpenAI client if key passes validation
    openai = new OpenAI({
      apiKey,
      dangerouslyAllowBrowser: true, // Required for client-side usage in React
    });
    console.log('OpenAI client initialized successfully');
  }
} catch (error) {
  console.error('Failed to initialize OpenAI client:', error);
  clientError = { type: 'initialization_error', message: 'Failed to initialize OpenAI client: ' + error?.message };
}

/**
 * Get client initialization status
 * @returns {Object} Status information about the OpenAI client
 */
export const getClientStatus = () => {
  if (clientError) {
    return { 
      initialized: false, 
      error: clientError,
      apiKey: import.meta.env?.VITE_OPENAI_API_KEY ? 'present' : 'missing'
    };
  }
  
  return { 
    initialized: !!openai, 
    error: null,
    apiKey: 'valid'
  };
};

/**
 * Validate API key format and basic requirements
 * @param {string} key - API key to validate
 * @returns {Object} Validation result
 */
export const validateApiKeyFormat = (key = import.meta.env?.VITE_OPENAI_API_KEY) => {
  if (!key) {
    return { valid: false, error: 'no_api_key', message: 'OpenAI API key not found in environment variables' };
  }
  
  if (key === 'your-openai-api-key-here') {
    return { valid: false, error: 'placeholder_key', message: 'API key is still a placeholder' };
  }
  
  if (!key?.startsWith('sk-')) {
    return { valid: false, error: 'invalid_format', message: 'Invalid API key format' };
  }
  
  if (key?.length < 20) {
    return { valid: false, error: 'invalid_length', message: 'API key appears to be too short' };
  }
  
  if (key?.startsWith('sk-proj-') && key?.length < 100) {
    return { valid: false, error: 'incomplete_project_key', message: 'Project-based API key appears to be incomplete' };
  }
  
  if (key?.includes(' ') || key?.includes('\n') || key?.includes('\t')) {
    return { valid: false, error: 'corrupted_key', message: 'API key contains invalid characters' };
  }
  
  return { valid: true };
};

/**
 * Test API key by making a simple API call
 * @returns {Promise<Object>} Test result
 */
export const testApiKey = async () => {
  if (!openai) {
    return { 
      success: false, 
      error: clientError || { type: 'client_not_initialized', message: 'OpenAI client not initialized' }
    };
  }
  
  try {
    // Make a simple API call to test the key
    const response = await openai?.models?.list();
    
    if (response && response?.data) {
      return { 
        success: true, 
        modelsAvailable: response?.data?.length,
        message: 'API key is valid and working'
      };
    } else {
      return { 
        success: false, 
        error: { type: 'unexpected_response', message: 'Unexpected response from OpenAI API' }
      };
    }
  } catch (error) {
    let errorType = 'unknown_error';
    let message = 'Unknown error occurred during API test';
    
    if (error?.status === 401) {
      errorType = 'invalid_key';
      message = 'API key is invalid or expired';
    } else if (error?.status === 429) {
      errorType = 'quota_exceeded';
      message = 'API quota exceeded or rate limit reached';
    } else if (error?.status === 403) {
      errorType = 'insufficient_permissions';
      message = 'API key lacks necessary permissions';
    } else if (error?.status >= 500) {
      errorType = 'server_error';
      message = 'OpenAI servers are experiencing issues';
    }
    
    return { 
      success: false, 
      error: { 
        type: errorType, 
        message,
        status: error?.status,
        originalError: error?.message
      }
    };
  }
};

/**
 * Reinitialize the client with a new API key
 * @param {string} newApiKey - New API key to use
 * @returns {Object} Initialization result
 */
export const reinitializeClient = (newApiKey) => {
  try {
    const validation = validateApiKeyFormat(newApiKey);
    if (!validation?.valid) {
      return { success: false, error: validation };
    }
    
    openai = new OpenAI({
      apiKey: newApiKey,
      dangerouslyAllowBrowser: true,
    });
    
    clientError = null;
    
    return { success: true, message: 'OpenAI client reinitialized successfully' };
  } catch (error) {
    console.error('Failed to reinitialize OpenAI client:', error);
    clientError = { type: 'reinitialization_error', message: 'Failed to reinitialize: ' + error?.message };
    return { success: false, error: clientError };
  }
};

export default openai;